'use client'

import './globals.css'
import { useState } from 'react'
import { usePathname } from 'next/navigation'
import { Providers } from './providers'
import { Header } from '@/components/layout/Header'
import { Sidebar, SidebarProvider, useSidebar } from '@/components/layout/Sidebar'
import { MobileSidebar } from '@/components/layout/MobileSidebar'
import { PointerGlow, VisualPattern } from '@/components/brand'
import { ShellFooter } from '@/components/layout/ShellFooter'
import { BrowserTitle } from '@/components/layout/BrowserTitle'
import { BreadcrumbProvider } from '@/context/BreadcrumbContext'
import { browserTitle } from '@/lib/naming'
import { Funnel_Display, Geologica } from 'next/font/google'
import { cn } from '@/lib/utils'

// Configure Funnel Display (Primary / Headings)
const funnel = Funnel_Display({
  subsets: ['latin'],
  variable: '--font-funnel',
  display: 'swap',
})

// Configure Geologica (Secondary / Body)
const geologica = Geologica({
  subsets: ['latin'],
  variable: '--font-geologica',
  display: 'swap',
})

// Routes that should NOT show the main app shell (sidebar, header)
const AUTH_ROUTES = ['/auth/signin', '/auth/register', '/auth/error']

// The requisitioner page is what a ward clerk sees, so it has its own minimal
// layout with no rail or header (plan 11 section 5), like the vendor portal.
const CHROMELESS_ROUTES = ['/supply-chain/ask']

// Inner layout that can access sidebar context and pathname
function LayoutContent({ children }: { children: React.ReactNode }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { isCollapsed } = useSidebar()
  const pathname = usePathname()

  // Check if current route is an auth route (should be full-screen)
  const isAuthRoute = AUTH_ROUTES.some((route) => pathname?.startsWith(route))

  // Auth routes get a clean, full-screen layout
  if (isAuthRoute) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100'>
        <VisualPattern variant='subtle' />
        <main className='relative z-10 flex min-h-screen items-center justify-center p-4'>
          {children}
        </main>
      </div>
    )
  }

  // The requisitioner page: the page wash and one column, no rail or header
  if (CHROMELESS_ROUTES.some((route) => pathname === route || pathname?.startsWith(`${route}/`))) {
    return (
      <div className='min-h-screen bg-background'>
        <VisualPattern variant='subtle' />
        <main
          id='main-content'
          role='main'
          aria-label='Main content'
          className='relative z-10 mx-auto flex min-h-screen w-full max-w-5xl flex-col px-4 py-8 sm:px-[26px]'
        >
          {children}
        </main>
      </div>
    )
  }

  // Normal app layout with sidebar and header
  return (
    <>
      <BrowserTitle />

      {/* Ambient visual pattern */}
      <VisualPattern variant='subtle' />

      {/* Cornflower light that follows the presenter's pointer */}
      <PointerGlow />

      {/* Desktop Sidebar */}
      <Sidebar />

      {/* Mobile Sidebar */}
      <MobileSidebar
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />

      {/* Main content area */}
      <div
        className={cn(
          'min-h-screen',
          // Offset for fixed sidebar - responds to collapse state
          isCollapsed ? 'md:pl-16' : 'md:pl-64',
          // Transition for sidebar collapse
          'transition-all duration-300 ease-out'
        )}
      >
        {/* Floating Header */}
        <Header onOpenMobileMenu={() => setMobileMenuOpen(true)} />

        {/* Main content with landmark role */}
        <main
          id='main-content'
          role='main'
          aria-label='Main content'
          className={cn(
            'flex flex-col',
            // Account for the floating header (52px tall, 12px from the top, 16px gap)
            'pt-20',
            // Page body padding: 26px sides, 16px under 640px
            'px-4 pb-8 sm:px-[26px]',
            // Min height for full viewport
            'min-h-screen'
          )}
          tabIndex={-1}
        >
          {/* Content fades and rises 8px on a route change (off under reduced motion). */}
          <div key={pathname} className='flex flex-1 animate-page-in flex-col'>
            {children}
          </div>
          <ShellFooter />
        </main>
      </div>
    </>
  )
}

export default function RootLayout({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <html lang='en' className='light' suppressHydrationWarning>
      <head>
        <title>{browserTitle()}</title>
        <meta name='description' content='Where Intelligence Meets Human' />
      </head>
      <body
        className={cn(
          'min-h-screen font-sans antialiased',
          'bg-background text-foreground',
          funnel.variable,
          geologica.variable
        )}
      >
        <Providers>
          <SidebarProvider>
            <BreadcrumbProvider>
              <LayoutContent>{children}</LayoutContent>
            </BreadcrumbProvider>
          </SidebarProvider>
        </Providers>
      </body>
    </html>
  )
}
